import React, {
  createContext,
  useContext,
  useRef,
  useState,
  useMemo,
  useCallback,
  useEffect,
} from "react";
import {
  useMutation,
  useSubscription,
  useQuery,
  useApolloClient,
  gql,
} from "@apollo/client";
import { useSnackbar } from "notistack";
import GET_ACTIVE_WHATSAPP_CALL from "src/graphql/query/chat/getActiveWhatsAppCall";
import GET_PENDING_WHATSAPP_CALLS from "src/graphql/query/chat/getPendingWhatsAppCalls";
import INITIATE_WHATSAPP_CALL from "src/graphql/mutation/chat/initiateWhatsAppCall";
import ACCEPT_WHATSAPP_CALL from "src/graphql/mutation/chat/acceptWhatsAppCall";
import REJECT_WHATSAPP_CALL from "src/graphql/mutation/chat/rejectWhatsAppCall";
import TERMINATE_WHATSAPP_CALL from "src/graphql/mutation/chat/terminateWhatsAppCall";
import WHATSAPP_CALL_SIGNALING from "src/graphql/subscriptions/chat/whatsappCallSignaling";
import WHATSAPP_PENDING_CALL_SIGNALING from "src/graphql/subscriptions/chat/whatsappPendingCallSignaling";
import useActiveUserDetail from "src/utils/shared/useActiveUserDetail";

const UPLOAD_CALL_RECORDING = gql`
  mutation UploadCallRecording($callId: ID!, $file: Upload!) {
    uploadCallRecording(callId: $callId, file: $file) {
      id
      callId
      recordingUrl
    }
  }
`;

const CALL_CHANNEL = "whatsapp_call_channel";

interface WebRTCContextType {
  callState: "idle" | "ringing" | "active" | "incoming";
  activeCall: any;
  incomingQueue: any[];
  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
  duration: number;
  formattedDuration: string;
  isStandalone: boolean;
  isMuted: boolean;
  // True while the post-call recording upload is in flight. The pop-out
  // window uses this to (a) show an "Uploading recording…" indicator,
  // (b) defer its auto-close timer, and (c) warn the user if they try to
  // close the window manually. Without this flag, the auto-close at 2s
  // after callState="idle" can abort the upload mid-stream, producing
  // the "Request disconnected during file upload stream parsing" error.
  isUploadingRecording: boolean;
  toggleMute: () => void;
  startCall: (inboxId: string) => Promise<void>;
  acceptCall: (callId?: string) => Promise<void>;
  rejectCall: () => Promise<void>;
  terminateCall: () => Promise<void>;
}

const WebRTCContext = createContext<WebRTCContextType | undefined>(undefined);

export const WebRTCProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const client = useApolloClient();
  const { agentId, mainSuperAdminId } = useActiveUserDetail();
  const [callState, setCallState] =
    useState<WebRTCContextType["callState"]>("idle");
  const [activeCall, setActiveCall] = useState<any>(null);
  const [incomingQueue, setIncomingQueue] = useState<any[]>([]);
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const [duration, setDuration] = useState(0);
  const [activeSince, setActiveSince] = useState<number | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isUploadingRecording, setIsUploadingRecording] = useState(false);
  // Ref mirror so the beforeunload listener (which captures state at mount)
  // can read the latest value without re-binding.
  const isUploadingRecordingRef = useRef(false);

  const isStandalone = useMemo(
    () => window.location.pathname === "/whatsapp-call-active",
    [],
  );
  const broadcastChannel = useRef<BroadcastChannel | null>(null);

  const peerConnection = useRef<RTCPeerConnection | null>(null);
  const lastHeartbeat = useRef<number>(Date.now());

  // 🎤 Call Recording Refs
  const mediaRecorder = useRef<MediaRecorder | null>(null);
  const audioChunks = useRef<Blob[]>([]);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const recordingCallIdRef = useRef<string | null>(null);

  const [initiateCall] = useMutation(INITIATE_WHATSAPP_CALL);
  const [acceptCallMutation] = useMutation(ACCEPT_WHATSAPP_CALL);
  const [rejectCallMutation] = useMutation(REJECT_WHATSAPP_CALL);
  const [terminateCallMutation] = useMutation(TERMINATE_WHATSAPP_CALL);
  const [uploadRecording] = useMutation(UPLOAD_CALL_RECORDING);

  const urlCallId = useMemo(
    () => new URLSearchParams(window.location.search).get("callId"),
    [],
  );

  // 🔄 Re-sync active call on page load/refresh
  const { data: activeCallData, refetch: refetchActiveCall } = useQuery(
    GET_ACTIVE_WHATSAPP_CALL,
    {
      variables: { callId: urlCallId },
      skip: !agentId,
      fetchPolicy: "network-only",
    },
  );

  const { data: pendingCallsData, refetch: refetchPendingCalls } = useQuery(
    GET_PENDING_WHATSAPP_CALLS,
    {
      skip: !agentId,
      fetchPolicy: "network-only",
    },
  );

  const cleanup = useCallback(() => {
    // 🎤 Stop Recording if active
    if (mediaRecorder.current && mediaRecorder.current.state !== "inactive") {
      mediaRecorder.current.stop();
    }

    if (peerConnection.current) {
      peerConnection.current.close();
      peerConnection.current = null;
    }
    if (localStream) {
      localStream.getTracks().forEach((track) => track.stop());
    }
    setLocalStream(null);
    setRemoteStream(null);
    setCallState("idle");
    setActiveCall(null);
    setIncomingQueue([]);
    setIsMuted(false);

    // 🛡️ Fix Resurrection: Manually clear the active call query result in the cache
    // This prevents the useEffect from restoring the call before the network refetch finishes.
    client.writeQuery({
      query: GET_ACTIVE_WHATSAPP_CALL,
      variables: { callId: urlCallId },
      data: { getActiveWhatsAppCall: null },
    });

    // 🔄 Refetch backend state to ensure no "ghost" calls remain in cache
    refetchActiveCall?.();
    refetchPendingCalls?.();
  }, [
    localStream,
    remoteStream, // Added to ensure stopRecording has access if needed
    refetchActiveCall,
    refetchPendingCalls,
    client,
    urlCallId,
  ]);

  const toggleMute = useCallback(async () => {
    if (!isStandalone) {
      // Main tab: Send command to pop-out
      broadcastChannel.current?.postMessage({ type: "TOGGLE_MUTE" });
      return;
    }

    const newMutedState = !isMuted;

    // Find the audio sender in the peer connection
    const audioSender = peerConnection.current
      ?.getSenders()
      .find((s) => s.track && s.track.kind === "audio");

    if (!audioSender) {
      console.warn("No audio sender found in peer connection.");
      return;
    }

    try {
      if (newMutedState) {
        // MUTE: Replace mic track with a silent track
        const silentCtx = new AudioContext();
        const destination = silentCtx.createMediaStreamDestination();
        const silentTrack = destination.stream.getAudioTracks()[0];
        await audioSender.replaceTrack(silentTrack);
        console.log("Microphone MUTED (replaced with silence)");
      } else {
        // UNMUTE: Restore the real microphone track
        const realMicTrack = localStream?.getAudioTracks()[0];
        if (realMicTrack) {
          await audioSender.replaceTrack(realMicTrack);
          console.log("Microphone UNMUTED (restored real mic)");
        } else {
          console.warn("Cannot unmute: no original mic track available.");
        }
      }

      setIsMuted(newMutedState);
      broadcastChannel.current?.postMessage({
        type: "MUTE_TOGGLE",
        payload: { isMuted: newMutedState },
      });
    } catch (err) {
      console.error("Failed to toggle mute:", err);
    }
  }, [isStandalone, localStream, isMuted]);

  const broadcastState = useCallback(() => {
    if (isStandalone && broadcastChannel.current) {
      broadcastChannel.current.postMessage({
        type: "STATE_UPDATE",
        payload: { callState, activeCall, duration, isMuted },
      });
    }
  }, [isStandalone, callState, activeCall, duration, isMuted]);

  const terminateCall = useCallback(async () => {
    if (!activeCall) return;

    if (!isStandalone && callState !== "idle") {
      // If we are in the main tab, tell the pop-out to terminate
      console.log(
        "[DEBUG-HANGUP] Path 3B: terminateCall() called inside Main Tab. Broadcasting TERMINATE_CALL to Pop-out.",
      );
      broadcastChannel.current?.postMessage({ type: "TERMINATE_CALL" });
      return;
    }

    try {
      console.log(
        "[DEBUG-HANGUP] Path 3A: terminateCall() called inside Pop-out (or standalone). Triggering terminateCallMutation.",
      );
      await terminateCallMutation({ variables: { callId: activeCall.callId } });
      broadcastChannel.current?.postMessage({ type: "CALL_ENDED" });
      refetchPendingCalls?.();
      cleanup();
    } catch (err) {
      console.error("Failed to terminate call", err);
    }
  }, [activeCall, cleanup, terminateCallMutation, isStandalone, callState]);

  // Create refs for callbacks and states to avoid recreating the BroadcastChannel or timers
  const cleanupRef = useRef(cleanup);
  const terminateCallRef = useRef(terminateCall);
  const toggleMuteRef = useRef(toggleMute);
  const broadcastStateRef = useRef(broadcastState);
  const activeCallRef = useRef(activeCall);
  const refetchActiveCallRef = useRef(refetchActiveCall);
  const terminateCallMutationRef = useRef(terminateCallMutation);

  // Keep refs up-to-date on every render
  useEffect(() => {
    cleanupRef.current = cleanup;
    terminateCallRef.current = terminateCall;
    toggleMuteRef.current = toggleMute;
    broadcastStateRef.current = broadcastState;
    activeCallRef.current = activeCall;
    refetchActiveCallRef.current = refetchActiveCall;
    terminateCallMutationRef.current = terminateCallMutation;
  });

  // 📡 Broadcast Channel Setup
  useEffect(() => {
    broadcastChannel.current = new BroadcastChannel(CALL_CHANNEL);

    const handleMessage = (event: MessageEvent) => {
      const { type, payload } = event.data;

      if (!isStandalone) {
        // Main Tab logic: Sync state from pop-out
        switch (type) {
          case "STATE_UPDATE":
            setCallState(payload.callState);
            setActiveCall(payload.activeCall);
            setDuration(payload.duration);
            setIsMuted(payload.isMuted);
            break;
          case "CALL_ENDED":
            cleanupRef.current();
            break;
          case "POP_OUT_CLOSED":
            // 🛑 Pop-out was closed manually.
            console.log(
              "[DEBUG-CLOSE] Main Tab received POP_OUT_CLOSED from BroadcastChannel. Payload:",
              payload,
            );
            // 1. Clear UI immediately
            cleanupRef.current();

            // 2. The main tab now takes over to ensure the mutation finishes on the backend.
            if (payload?.callId) {
              terminateCallMutationRef
                .current({ variables: { callId: payload.callId } })
                .then(() => {
                  refetchActiveCallRef.current?.();
                })
                .catch((err) => {
                  console.error("Main tab failed to terminate call:", err);
                  refetchActiveCallRef.current?.();
                });
            }
            break;
          case "MUTE_TOGGLE":
            setIsMuted(payload.isMuted);
            break;
          case "HEARTBEAT":
            lastHeartbeat.current = Date.now();
            break;
          default:
            break;
        }
      } else {
        // Pop-out logic: Handle commands from main tab
        switch (type) {
          case "TERMINATE_CALL":
            terminateCallRef.current();
            break;
          case "TOGGLE_MUTE":
            toggleMuteRef.current();
            break;
          case "SYNC_REQUEST":
            broadcastStateRef.current();
            break;
          default:
            break;
        }
      }
    };

    if (broadcastChannel.current) {
      broadcastChannel.current.onmessage = handleMessage;
    }

    // If we just opened the main tab, ask if there is an active call in a pop-out
    if (!isStandalone && broadcastChannel.current) {
      broadcastChannel.current.postMessage({ type: "SYNC_REQUEST" });
    }

    return () => {
      broadcastChannel.current?.close();
    };
  }, [isStandalone]);

  // 🚪 Handle Manual Window Closure (Pop-out Window)
  useEffect(() => {
    if (isStandalone) {
      const handleBeforeUnload = (event: BeforeUnloadEvent) => {
        const currentActiveCall = activeCallRef.current;
        console.log(
          "[DEBUG-CLOSE] beforeunload listener fired in Pop-out. Active Call ID:",
          currentActiveCall?.callId,
        );
        if (currentActiveCall?.callId) {
          console.log(
            "[DEBUG-CLOSE] Pop-out sending POP_OUT_CLOSED to Main Tab",
          );
          // 📢 Tell the main tab that we are closing, so it can handle the mutation
          broadcastChannel.current?.postMessage({
            type: "POP_OUT_CLOSED",
            payload: { callId: currentActiveCall.callId },
          });
        }
        // If the post-call recording upload is still in flight, ask the
        // user to confirm. Closing the window mid-upload aborts the
        // request and the chat bubble never gets its recording. Modern
        // browsers show a generic confirmation regardless of the string,
        // but we still need to set returnValue + return a string to
        // trigger the prompt.
        if (isUploadingRecordingRef.current) {
          const message =
            "Recording is still uploading. Leave anyway and lose the recording?";
          event.preventDefault();
          // @ts-ignore — legacy returnValue field still required in some browsers
          event.returnValue = message;
          return message;
        }
        return undefined;
      };

      window.addEventListener("beforeunload", handleBeforeUnload);
      return () => {
        window.removeEventListener("beforeunload", handleBeforeUnload);
      };
    }
    return undefined;
  }, [isStandalone]);

  // 💓 Heartbeat Mechanism: Detects if the pop-out window is closed or crashes
  // 1. Pop-out Side: Send heartbeat every 2 seconds
  useEffect(() => {
    if (isStandalone && callState !== "idle") {
      const interval = setInterval(() => {
        broadcastChannel.current?.postMessage({ type: "HEARTBEAT" });
      }, 2000);

      return () => clearInterval(interval);
    }
    return undefined;
  }, [isStandalone, callState]);

  // 2. Main Tab Side: Monitor heartbeat every 3 seconds
  useEffect(() => {
    // 🛡️ Busy Guard: Only monitor heartbeats if the call is ACTIVE or RINGING (outgoing).
    if (!isStandalone && (callState === "active" || callState === "ringing")) {
      // Reset heartbeat on mount/state change to active
      lastHeartbeat.current = Date.now();

      const interval = setInterval(() => {
        // 🧪 Refactoring to Backend-Authoritative Model:
        // We no longer trust this timer to kill the call if the tab is throttled/backgrounded.
        if (document.visibilityState !== "visible") {
          return; // Ignore heartbeat checks while tab is in background to prevent false-positives
        }

        const timeSinceLastHeartbeat = Date.now() - lastHeartbeat.current;

        // 🛡️ Grace Period: If no heartbeat for 30s, verify with backend before taking action
        if (timeSinceLastHeartbeat > 30000) {
          console.warn(
            `Call heartbeat lost (${timeSinceLastHeartbeat}ms). Verifying authoritative state with server...`,
          );

          // Silent Resync: Check if the backend still thinks the call is active
          refetchActiveCallRef.current?.().then(({ data }) => {
            if (!data?.getActiveWhatsAppCall) {
              console.log(
                "[DEBUG-HEARTBEAT] Server confirms call is gone. Cleaning up frontend state.",
              );
              cleanupRef.current();
            } else {
              console.log(
                "[DEBUG-HEARTBEAT] Server says call is still active. Preserving state despite lost heartbeat.",
              );
              // Gently reset heartbeat to avoid spamming the server
              lastHeartbeat.current = Date.now();
            }
          });
        }
      }, 5000);

      return () => clearInterval(interval);
    }
    return undefined;
  }, [isStandalone, callState]);

  // Broadcast state whenever it changes in the pop-out
  useEffect(() => {
    if (isStandalone) {
      broadcastState();
    }
  }, [isStandalone, broadcastState]);

  // 🎤 Recording Lifecycle Logic (ONLY in Pop-out Window)
  useEffect(() => {
    console.log("call_recording__", isStandalone, localStream, remoteStream);
    if (!isStandalone || !localStream || !remoteStream) return;

    console.log("call_recording__ gotinside", callState);

    if (callState === "active") {
      // START RECORDING
      console.log("🎤 Starting Call Recording...");
      try {
        const audioCtx = new (window.AudioContext ||
          (window as any).webkitAudioContext)();
        audioCtxRef.current = audioCtx;

        const dest = audioCtx.createMediaStreamDestination();
        const localSource = audioCtx.createMediaStreamSource(localStream);
        const remoteSource = audioCtx.createMediaStreamSource(remoteStream);

        localSource.connect(dest);
        remoteSource.connect(dest);

        const recorder = new MediaRecorder(dest.stream, {
          mimeType: "audio/webm",
        });
        audioChunks.current = [];

        recorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            audioChunks.current.push(event.data);
          }
        };

        recorder.onstop = async () => {
          console.log("🎤 Recording stopped. Uploading...");
          const audioBlob = new Blob(audioChunks.current, {
            type: "audio/webm",
          });
          const callId = recordingCallIdRef.current;

          if (callId && audioChunks.current.length > 0) {
            const file = new File([audioBlob], `recording-${callId}.webm`, {
              type: "audio/webm",
            });

            // Mark upload in flight so the pop-out's auto-close timer
            // defers, the UI shows an "Uploading recording…" indicator,
            // and the beforeunload listener can warn on manual close.
            isUploadingRecordingRef.current = true;
            setIsUploadingRecording(true);
            try {
              await uploadRecording({
                variables: { callId, file },
              });
              console.log("🎤 WebRTC: Call recording uploaded successfully.");
            } catch (err) {
              console.error("🎤 WebRTC: Failed to upload call recording:", err);
            } finally {
              isUploadingRecordingRef.current = false;
              setIsUploadingRecording(false);
            }
          }
          audioChunks.current = [];
          recordingCallIdRef.current = null;
          if (audioCtxRef.current) {
            audioCtxRef.current.close();
            audioCtxRef.current = null;
          }
        };

        recordingCallIdRef.current = activeCallRef.current?.callId;
        recorder.start(1000); // Collect data every 1s
        mediaRecorder.current = recorder;
      } catch (err) {
        console.error("🎤 WebRTC: Failed to start recorder:", err);
      }
    } else if (
      callState === "idle" &&
      mediaRecorder.current?.state === "recording"
    ) {
      // STOP RECORDING
      mediaRecorder.current.stop();
    }
  }, [isStandalone, callState, localStream, remoteStream, uploadRecording]);

  // 🔄 Timer Logic: Calculates talk time based on activeSince (ONLY inside the Pop-out Window)
  useEffect(() => {
    let interval: any;

    if (isStandalone && callState === "active" && activeSince) {
      // Initial calculation
      setDuration(Math.floor((Date.now() - activeSince) / 1000));

      interval = setInterval(() => {
        const now = Date.now();
        setDuration(Math.floor((now - activeSince) / 1000));
      }, 1000);
    } else if (callState !== "active") {
      setDuration(0);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isStandalone, callState, activeSince]);

  // 🔄 Re-sync activeSince when activeCall or state changes (ONLY inside the Pop-out Window)
  useEffect(() => {
    if (isStandalone && callState === "active" && !activeSince) {
      const parseDate = (dateStr: any) => {
        if (!dateStr) return null;
        const timestamp = Date.parse(dateStr);
        if (!Number.isNaN(timestamp)) return timestamp;
        if (!Number.isNaN(Number(dateStr))) {
          const num = Number(dateStr);
          return num > 10000000000 ? num : num * 1000;
        }
        return null;
      };

      const startTime = parseDate(activeCall?.updatedAt);
      if (startTime) {
        setActiveSince(startTime);
      } else {
        setActiveSince(Date.now());
      }
    } else if (callState !== "active" && activeSince) {
      setActiveSince(null);
    }
  }, [isStandalone, callState, activeCall?.updatedAt, activeSince]);

  const formattedDuration = useMemo(() => {
    const minutes = Math.floor(duration / 60);
    const seconds = duration % 60;
    return `${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
  }, [duration]);

  // 🔄 Re-sync active call on page load/refresh
  useEffect(() => {
    if (activeCallData?.getActiveWhatsAppCall && callState === "idle") {
      const call = activeCallData.getActiveWhatsAppCall;
      console.log("Restoring active call state after refresh:", call);
      setActiveCall(call);

      if (call.status === "ringing") {
        const isIncoming = call.direction !== "BUSINESS_INITIATED";
        setCallState(isIncoming ? "incoming" : "ringing");

        // 🛡️ Fix: If it's an incoming call, we must add it to the queue
        if (isIncoming) {
          setIncomingQueue((prev) => {
            const exists = prev.some((c) => c.callId === call.callId);
            if (exists) return prev;
            return [call, ...prev];
          });
        }
      } else if (call.status === "accepted" || call.status === "connected") {
        setCallState("active");
      }
    }
  }, [activeCallData, callState]);

  // 🔄 Re-sync all pending (ringing) calls on page load/refresh
  useEffect(() => {
    if (
      pendingCallsData?.getPendingWhatsAppCalls &&
      callState !== "active" // 🛡️ Don't force sync while in a call
    ) {
      const calls = pendingCallsData.getPendingWhatsAppCalls;
      console.log("Synchronizing pending call queue with server:", calls);

      setIncomingQueue((prev) => {
        // 🛡️ Logic Change: Sync the queue with the server state.
        const serverCallIds = new Set(calls.map((c: any) => c.callId));

        const existingIds = new Set(prev.map((c) => c.callId));
        const newCalls = calls.filter((c: any) => !existingIds.has(c.callId));

        // 🔄 Filter: Also remove calls from local queue if the server definitely knows they are gone
        // This ensures that stale modals are cleared once the query refetch completes.
        const filteredPrev = prev
          .filter((c) => serverCallIds.has(c.callId))
          .map((c) => {
            // 🛡️ Merge logic: If the call is still present on server, merge its data (e.g. newly linked inboxId)
            const serverCall = calls.find((sc: any) => sc.callId === c.callId);
            return serverCall ? { ...c, ...serverCall } : c;
          });

        if (newCalls.length === 0 && filteredPrev.length === prev.length)
          return prev;
        return [...filteredPrev, ...newCalls];
      });

      if (callState === "idle" && calls.length > 0 && !activeCall) {
        setActiveCall(calls[0]);
        setCallState("incoming");
      }
    }
  }, [pendingCallsData]); // 🛡️ Fix: Removed callState dependency to prevent "resurrection" during cleanup transitions

  const handleSignaling = useCallback(
    async (callData: any) => {
      // If this is an update for the currently active call, refresh the data (e.g. newly linked inboxId)
      setActiveCall((prev: any) => {
        if (
          prev &&
          (prev.callId === callData.callId || prev.id === callData.id)
        ) {
          return { ...prev, ...callData };
        }
        return prev;
      });

      // 🔄 Update the incoming queue with the new data (e.g. linked inboxId)
      setIncomingQueue((prev) =>
        prev.map((c) =>
          c.callId === callData.callId || c.id === callData.id
            ? { ...c, ...callData }
            : c,
        ),
      );

      if (
        callData.status === "offer" &&
        callData.direction === "USER_INITIATED"
      ) {
        // 🛡️ Busy Guard: If the agent is already talking, ignore/auto-reject should happen at backend.
        // However, if we are just "incoming" or "idle", we add to the queue to show multiple modals.
        if (callState === "active") {
          console.warn("Agent is busy talking. Ignoring secondary call offer.");
          return;
        }

        // Add to incoming queue if not already present
        setIncomingQueue((prev) => {
          const exists = prev.some((c) => c.callId === callData.callId);
          if (exists) return prev;
          return [...prev, callData];
        });

        // If we are idle, show this as the primary call
        if (callState === "idle") {
          setActiveCall(callData);
          setCallState("incoming");
        }
        return;
      }

      if (
        [
          "rejected",
          "terminated",
          "failed",
          "COMPLETED",
          "completed",
          "CLAIMED",
        ].includes(callData.status)
      ) {
        // Remove from queue
        setIncomingQueue((prev) =>
          prev.filter((c) => c.callId !== callData.callId),
        );

        // 🔄 Refetch pending calls from server to ensure Apollo cache is updated
        // and doesn't re-add this call on the next sync/re-render.
        refetchPendingCalls?.();

        // If the call was claimed by someone else or terminated, clear our modal if it was the active one.
        if (
          activeCall &&
          (activeCall.callId === callData.callId ||
            activeCall.id === callData.id)
        ) {
          console.log("Call terminated or claimed by someone else.");
          cleanup();

          // 🛡️ If there are other calls in the queue, pick the next one
          setIncomingQueue((prev) => {
            if (prev.length > 0) {
              const nextCall = prev[0];
              setActiveCall(nextCall);
              setCallState("incoming");
            }
            return prev;
          });
        }
        return;
      }

      // 📡 Signaling Update (SDP)
      // If we receive an SDP answer, we MUST apply it to hear early media (ringing tones)
      if (callData.sdpAnswer && peerConnection.current) {
        try {
          if (peerConnection.current.signalingState === "have-local-offer") {
            await peerConnection.current.setRemoteDescription(
              new RTCSessionDescription({
                type: "answer",
                sdp: callData.sdpAnswer,
              }),
            );
            console.log("Applied Remote SDP Answer (Early Media enabled)");
          }
        } catch (err) {
          console.error("Error applying remote description:", err);
        }
      }

      // 🚦 State Transition (UI)
      if (
        callData.status === "accepted" ||
        callData.status === "connected" ||
        (callData.status === "answer" &&
          callData.direction === "USER_INITIATED")
      ) {
        console.log("checking_3 - Transitioning to ACTIVE");
        setCallState("active");
      }
    },
    [localStream, agentId, cleanup, isStandalone, callState, activeCall],
  );

  // 📡 Subscriptions (Only run in the window responsible for signaling)
  // For simplicity, we'll let both run, but only the pop-out or the one with active media will handle SDP.

  // Signalling for specific agent
  useSubscription(WHATSAPP_CALL_SIGNALING, {
    variables: { agentId },
    onData: ({ data }) => {
      const callData = data.data.whatsappCallSignaling;
      handleSignaling(callData);
    },
    skip: !agentId,
  });

  // Signaling for pending calls (for all agents in org)
  useSubscription(WHATSAPP_PENDING_CALL_SIGNALING, {
    variables: { mainSuperAdminId },
    onData: ({ data }) => {
      const callData = data.data.whatsappPendingCallSignaling;
      handleSignaling(callData);
    },
    skip: !mainSuperAdminId,
  });

  const setupPeerConnection = useCallback(async () => {
    const pc = new RTCPeerConnection({
      iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
    });

    pc.ontrack = (event) => {
      setRemoteStream(event.streams[0]);
    };

    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    setLocalStream(stream);
    stream.getTracks().forEach((track) => pc.addTrack(track, stream));

    peerConnection.current = pc;
    return pc;
  }, []);

  const checkMicrophonePermission = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Immediately stop the stream as we only needed to check permission
      stream.getTracks().forEach((track) => track.stop());
      return true;
    } catch (err: any) {
      console.error("Microphone permission check failed:", err);
      if (
        err.name === "NotAllowedError" ||
        err.name === "PermissionDeniedError"
      ) {
        enqueueSnackbar(
          "Microphone blocked! Please allow microphone access in your browser settings to make calls.",
          { variant: "error", autoHideDuration: 6000 },
        );
      } else {
        enqueueSnackbar(
          "Could not access microphone. Please check your audio devices.",
          {
            variant: "error",
          },
        );
      }
      return false;
    }
  }, [enqueueSnackbar]);

  const startCall = useCallback(
    async (inboxId: string) => {
      // Permission check before opening pop-out or starting media
      const hasPermission = await checkMicrophonePermission();
      if (!hasPermission) return;

      if (!isStandalone) {
        // Main Tab: Open pop-out
        const popup = window.open(
          `/whatsapp-call-active?inboxId=${inboxId}&action=start`,
          "WhatsAppCall",
          "width=400,height=600,menubar=no,status=no,location=no",
        );
        if (popup) popup.focus();
        return;
      }

      try {
        const pc = await setupPeerConnection();
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);

        const { data } = await initiateCall({
          variables: { inboxId, sdpOffer: offer.sdp },
        });

        setActiveCall(data.initiateWhatsAppCall);
        console.log("checking_4");
        setCallState("ringing");
      } catch (err: any) {
        console.error("Failed to start call", err);
        enqueueSnackbar("Failed to start call. Please try again.", {
          variant: "error",
        });
        cleanup();
        throw err;
      }
    },
    [
      cleanup,
      initiateCall,
      setupPeerConnection,
      checkMicrophonePermission,
      enqueueSnackbar,
      isStandalone,
    ],
  );

  const acceptCall = useCallback(
    async (targetCallId?: string) => {
      let callToAccept: any = null;

      if (targetCallId) {
        callToAccept = incomingQueue.find((c) => c.callId === targetCallId);
        // Also check if it's the currently active call being restored
        if (!callToAccept && activeCall?.callId === targetCallId) {
          callToAccept = activeCall;
        }
      } else if (activeCall) {
        callToAccept = activeCall;
      } else if (incomingQueue.length > 0) {
        const [firstCall] = incomingQueue;
        callToAccept = firstCall;
      }

      if (!callToAccept) {
        console.warn("No call to accept matching:", targetCallId);
        return;
      }

      if (!isStandalone) {
        // Main Tab: Check permission
        const hasPermission = await checkMicrophonePermission();
        if (!hasPermission) return;

        // 🛡️ Multi-Call Rejection: Reject all other callers in the queue BEFORE opening pop-out
        const otherCallers = incomingQueue.filter(
          (c) => c.callId !== callToAccept.callId,
        );
        if (otherCallers.length > 0) {
          console.log(
            `Main Tab: Auto-rejecting ${otherCallers.length} other pending callers...`,
          );
          await Promise.all(
            otherCallers.map((c) =>
              rejectCallMutation({ variables: { callId: c.callId } }),
            ),
          );
        }
        // Open pop-out with specific callId
        const popup = window.open(
          `/whatsapp-call-active?callId=${callToAccept.callId}&action=accept`,
          "WhatsAppCall",
          "width=400,height=600,menubar=no,status=no,location=no",
        );

        if (popup) {
          popup.focus();
          // 🛡️ Smooth Transition: Immediately update main tab state to "Active"
          // This prevents the incoming UI from flickering back while the pop-out is loading.
          setActiveCall(callToAccept);
          setCallState("active");
          setIncomingQueue([]); // Clear the local queue immediately
          // (Note: We avoid immediate refetchPendingCalls here to prevent the race condition
          // where the ringing call might briefly re-appear before the mutation finishes).
        } else {
          enqueueSnackbar(
            "Pop-up blocked! Please allow pop-ups for this site to answer calls.",
            { variant: "error" },
          );
        }
        return;
      }

      try {
        const pc = await setupPeerConnection();
        await pc.setRemoteDescription(
          new RTCSessionDescription({
            type: "offer",
            sdp: callToAccept.sdpOffer,
          }),
        );

        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);

        // (Note: Rejection of others now happens in the main tab to ensure the queue is known)
        setIncomingQueue([]); // Clear the queue in pop-out too

        await acceptCallMutation({
          variables: { callId: callToAccept.callId, sdpAnswer: answer.sdp },
        });

        console.log("checking_5");
        setActiveCall((prev: any) => ({
          ...prev,
          updatedAt: new Date().toISOString(),
        }));
        setCallState("active");
      } catch (err: any) {
        console.error("Failed to accept call", err);
        cleanup();
        // Throw the error so the UI can catch and display the specific message
        throw err;
      }
    },
    [
      activeCall,
      incomingQueue,
      cleanup,
      acceptCallMutation,
      setupPeerConnection,
      checkMicrophonePermission,
      isStandalone,
      rejectCallMutation,
    ],
  );

  const rejectCall = useCallback(async () => {
    if (!activeCall) return;
    try {
      await rejectCallMutation({ variables: { callId: activeCall.callId } });
      refetchPendingCalls?.();
      cleanup();
    } catch (err) {
      console.error("Failed to reject call", err);
    }
  }, [activeCall, cleanup, rejectCallMutation]);

  const contextValue = useMemo(
    () => ({
      callState,
      activeCall,
      incomingQueue,
      localStream,
      remoteStream,
      duration,
      formattedDuration,
      isStandalone,
      isMuted,
      isUploadingRecording,
      toggleMute,
      startCall,
      acceptCall,
      rejectCall,
      terminateCall,
    }),
    [
      callState,
      activeCall,
      incomingQueue,
      localStream,
      remoteStream,
      duration,
      formattedDuration,
      isStandalone,
      isMuted,
      isUploadingRecording,
      toggleMute,
      startCall,
      acceptCall,
      rejectCall,
      terminateCall,
    ],
  );

  return (
    <WebRTCContext.Provider value={contextValue}>
      {children}
    </WebRTCContext.Provider>
  );
};

export const useWebRTC = () => {
  const context = useContext(WebRTCContext);
  if (!context)
    throw new Error("useWebRTC must be used within a WebRTCProvider");
  return context;
};
